import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Camera,
  CheckCircle2,
  ChevronRight,
  FileText,
  Images,
  PlayCircle,
  RefreshCw,
  Send,
  Trash2,
  User,
} from "lucide-react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Loader } from "@/components/ui/Loader";
import { ROUTES } from "@/config/constants";
import { useAuth } from "@/hooks/useAuth";
import { useAppAccess } from "@/hooks/useAppAccess";
import { useModules } from "@/context/ModulesContext";
import { fetchAllUnitsForRegistry, type ComercialUnitRow } from "@/services/comercial";
import {
  deleteChaoReport,
  fetchChaoUnitDetail,
  reportIsPending,
  startChaoReportSession,
  type ChaoReportSummaryDto,
} from "@/services/chaoDeObra";
import { formatDateTime } from "@/utils/format";
import { cn } from "@/utils/cn";
import { ChaoDeObraReportModal } from "./ChaoDeObraReportModal";
import { ChaoReportWorkModal, type ChaoReportWorkTab } from "./ChaoReportWorkModal";

function unitBadgeLabel(name: string, unitId: string, stateBadge?: number | string): string {
  if (stateBadge != null && String(stateBadge).trim()) return String(stateBadge);
  const tail = (name.split("—").pop() ?? name.split("-").pop() ?? name).trim();
  const num = tail.match(/(\d+)/)?.[1];
  if (num) return num;
  return unitId.length > 2 ? unitId.slice(-2) : unitId;
}

export function ChaoDeObraUnitPage() {
  const { unitId: unitIdParam } = useParams<{ unitId: string }>();
  const unitId = unitIdParam ? decodeURIComponent(unitIdParam) : "";
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { isAdmin, can } = useAppAccess();
  const canReview = can("chaoDeObraAprovar");
  const { chaoDeObra } = useModules();
  const { setUnitTransitionLoading, unitTransitionLoading } = chaoDeObra;

  const [unitRow, setUnitRow] = useState<ComercialUnitRow | null>(null);
  const [generalPct, setGeneralPct] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(
    () => (location.state as { okMsg?: string } | null)?.okMsg ?? null,
  );
  const [reports, setReports] = useState<ChaoReportSummaryDto[]>([]);
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [modalReportId, setModalReportId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<ChaoReportWorkTab>("photos");
  const [galleryReport, setGalleryReport] = useState<ChaoReportSummaryDto | null>(null);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);

  const stateBadge = (location.state as { unitBadge?: number | string } | null)?.unitBadge;

  const load = useCallback(async () => {
    if (!unitId) {
      setLoading(false);
      setUnitTransitionLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [list, detail] = await Promise.all([
        fetchAllUnitsForRegistry(),
        fetchChaoUnitDetail(unitId),
      ]);
      setUnitRow(list.find((u) => u.id === unitId) ?? null);
      if (!detail.success) {
        setError("Resposta inválida da API.");
        return;
      }
      setReports(detail.reports ?? []);
      setActiveReportId(detail.activeReportId ?? null);
      setGeneralPct(Number(detail.generalPct) || 0);
      setStatus(detail.status ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar unidade.");
    } finally {
      setLoading(false);
      setUnitTransitionLoading(false);
    }
  }, [unitId, setUnitTransitionLoading]);

  useEffect(() => {
    void load();
    return () => setUnitTransitionLoading(false);
  }, [load, setUnitTransitionLoading]);

  const reportFromUrl = searchParams.get("relatorio");
  const tabFromUrl = searchParams.get("aba") === "progress" ? "progress" : "photos";

  useEffect(() => {
    if (reportFromUrl && !loading) {
      setModalReportId(reportFromUrl);
      setModalTab(tabFromUrl);
    }
  }, [reportFromUrl, tabFromUrl, loading]);

  const openReport = (reportId: string, tab: ChaoReportWorkTab = "photos") => {
    setModalReportId(reportId);
    setModalTab(tab);
    setSearchParams({ relatorio: reportId, aba: tab }, { replace: true });
  };

  const closeModal = () => {
    setModalReportId(null);
    setSearchParams({}, { replace: true });
  };

  const onStartReport = async () => {
    if (!unitId) return;
    setSessionBusy(true);
    setOkMsg(null);
    setError(null);
    try {
      const res = await startChaoReportSession(unitId);
      if (!res.success || !res.report?.id) {
        setError(res.error ?? "Não foi possível iniciar o registro.");
        return;
      }
      setActiveReportId(res.report.id);
      await load();
      openReport(res.report.id, "photos");
    } finally {
      setSessionBusy(false);
    }
  };

  const activeReport = useMemo(
    () => reports.find((r) => r.id === activeReportId) ?? null,
    [reports, activeReportId],
  );

  const sessionManager = activeReport?.responsavel ?? user?.displayName ?? "—";
  const sessionRole =
    user?.role === "admin" ? "master" : user?.role ? user.role.toUpperCase() : "";

  const unitName = unitRow?.name ?? `Unidade ${unitId}`;
  const badge = unitBadgeLabel(unitName, unitId, stateBadge);

  const onReportSubmitted = () => {
    setOkMsg("Registro enviado para aprovação. O avanço ainda não foi computado.");
    void load();
    closeModal();
  };

  const onReportApproved = () => {
    setOkMsg("Registro aprovado. O avanço foi computado na medição.");
    void load();
    closeModal();
  };

  const onReportReturned = () => {
    setOkMsg("Registro devolvido para ajuste.");
    void load();
  };

  const onDeleteReport = async (rep: ChaoReportSummaryDto) => {
    if (!unitId || !isAdmin) return;
    const label = rep.isOpen
      ? "registro em andamento"
      : `registro de ${formatDateTime(rep.at)}`;
    if (
      !window.confirm(
        `Excluir permanentemente este ${label}? Todas as fotos e o avanço associados serão removidos.`,
      )
    ) {
      return;
    }
    setDeletingReportId(rep.id);
    setError(null);
    setOkMsg(null);
    try {
      const res = await deleteChaoReport(unitId, rep.id);
      if (!res.success) {
        setError(res.error ?? "Não foi possível excluir o registro.");
        return;
      }
      if (modalReportId === rep.id) closeModal();
      if (galleryReport?.id === rep.id) setGalleryReport(null);
      setOkMsg("Registro excluído.");
      await load();
    } finally {
      setDeletingReportId(null);
    }
  };

  if (!unitId) {
    return (
      <p className="text-sm text-slate-500">
        Unidade inválida.{" "}
        <Link to={ROUTES.CHAO_DE_OBRA} className="text-brand-600 font-medium">
          Voltar
        </Link>
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-10 w-full min-w-0">
      <Link
        to={ROUTES.CHAO_DE_OBRA}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-brand-700 w-fit"
      >
        <ArrowLeft className="w-4 h-4" />
        Todas as unidades
      </Link>

      {loading && !unitTransitionLoading ? (
        <div className="flex flex-col items-center justify-center gap-4 min-h-[min(50vh,420px)] py-16 rounded-2xl border border-slate-100 bg-slate-50/50">
          <Loader size="lg" color="brand" label="Carregando unidade..." />
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-card">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
              <div className="flex gap-4 min-w-0 flex-1">
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-violet-100 text-violet-800 font-black text-lg sm:text-xl flex items-center justify-center tabular-nums">
                  {badge}
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">{unitName}</h2>
                  <p className="text-sm text-slate-500 mt-1">ID Sienge: {unitId}</p>
                  <p className="flex flex-wrap items-center gap-1.5 text-sm text-slate-600 mt-3">
                    <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>
                      Responsável da sessão:{" "}
                      <strong className="text-slate-800">{sessionManager}</strong>
                      {sessionRole ? (
                        <span className="text-slate-500 font-normal"> ({sessionRole})</span>
                      ) : null}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-stretch sm:items-end gap-2 lg:min-w-[200px]">
                <div className="rounded-xl bg-slate-900 text-white px-5 py-4 w-full sm:w-[220px]">
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-300">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Execução geral
                  </p>
                  <p className="text-4xl sm:text-5xl font-black tabular-nums mt-1 leading-none">{generalPct}%</p>
                  <p className="text-xs text-slate-300 mt-2 leading-snug">{status || "Sem lançamento de avanço"}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void load()}
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                  className="text-brand-600 hover:text-brand-700 hover:bg-brand-50 self-end"
                >
                  Recarregar
                </Button>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {okMsg && (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-600" />
              {okMsg}
            </div>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-brand-600" />
                  Histórico de relatórios
                </h3>
                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                  Inicie um registro, envie as fotos por ambiente e envie para aprovação. O avanço só entra na medição
                  depois que um administrador aprovar.
                </p>
              </div>
              {!activeReportId && (
                <Button
                  variant="primary"
                  size="sm"
                  className="shrink-0 min-h-[40px]"
                  leftIcon={<PlayCircle className="w-4 h-4" />}
                  onClick={() => void onStartReport()}
                  loading={sessionBusy}
                >
                  Iniciar registro
                </Button>
              )}
            </div>

            <ul className="space-y-2 max-h-[min(60vh,480px)] overflow-y-auto pr-1">
              {reports.map((rep) => {
                const pending = reportIsPending(rep);
                return (
                <li key={rep.id}>
                  <div
                    className={cn(
                      "flex items-center gap-2 sm:gap-3 rounded-xl border transition-colors",
                      pending
                        ? "border-amber-200 bg-amber-50/80"
                        : rep.isOpen
                          ? "border-emerald-200 bg-emerald-50/80"
                          : "border-slate-100 bg-slate-50/60",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => openReport(rep.id, "photos")}
                      className="flex-1 flex items-center gap-3 text-left px-3 py-3 min-w-0"
                    >
                      <span
                        className={cn(
                          "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center",
                          pending
                            ? "bg-amber-100 text-amber-700"
                            : rep.isOpen
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-200 text-slate-600",
                        )}
                      >
                        {pending || rep.isOpen ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "text-sm font-semibold leading-snug",
                            pending ? "text-amber-950" : rep.isOpen ? "text-emerald-900" : "text-slate-800",
                          )}
                        >
                          {pending
                            ? `Aguardando aprovação — ${formatDateTime(rep.at)}`
                            : rep.isOpen
                              ? `Registro em andamento — ${formatDateTime(rep.startedAt)}`
                              : `Registro concluído — ${formatDateTime(rep.at)}`}
                        </p>
                        <p
                          className={cn(
                            "text-xs mt-1 flex flex-wrap items-center gap-x-1.5",
                            pending ? "text-amber-800/90" : rep.isOpen ? "text-emerald-800/90" : "text-slate-500",
                          )}
                        >
                          <span className="inline-flex items-center gap-1">
                            <Camera className="w-3 h-3" />
                            {rep.photoCount} foto{rep.photoCount !== 1 ? "s" : ""}
                          </span>
                          <span>·</span>
                          <span>{rep.responsavel}</span>
                          {!rep.isOpen && !pending && rep.startedAt !== rep.at && (
                            <>
                              <span>·</span>
                              <span>Iniciado {formatDateTime(rep.startedAt)}</span>
                            </>
                          )}
                          {rep.generalPct != null && pending && (
                            <>
                              <span>·</span>
                              <span>{rep.generalPct}% proposto</span>
                            </>
                          )}
                          {rep.generalPct != null && !rep.isOpen && !pending && (
                            <>
                              <span>·</span>
                              <span>{rep.generalPct}% geral</span>
                            </>
                          )}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 hidden sm:block" />
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0 pr-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "border font-semibold",
                          rep.isOpen || pending
                            ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                            : "border-brand-200 bg-brand-50/80 text-brand-800 hover:bg-brand-100",
                        )}
                        leftIcon={<Images className="w-4 h-4" />}
                        title="Ver todas as fotos do relatório em sequência"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGalleryReport(rep);
                        }}
                      >
                        <span className="sr-only sm:not-sr-only sm:inline">Ver fotos</span>
                      </Button>
                      {rep.isOpen && !pending && rep.id === activeReportId && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 font-semibold"
                          leftIcon={<Send className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openReport(rep.id, "progress");
                          }}
                        >
                          Enviar
                        </Button>
                      )}
                      {pending && canReview && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 font-semibold"
                          leftIcon={<CheckCircle2 className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openReport(rep.id, "progress");
                          }}
                        >
                          Avaliar
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="border border-red-200 text-red-700 hover:bg-red-50"
                          title="Excluir registro (administrador)"
                          loading={deletingReportId === rep.id}
                          leftIcon={<Trash2 className="w-4 h-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            void onDeleteReport(rep);
                          }}
                        >
                          <span className="sr-only">Excluir</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
                );
              })}
              {reports.length === 0 && (
                <li className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl">
                  Nenhum registro ainda. Clique em <strong>Iniciar registro</strong> para começar.
                </li>
              )}
            </ul>
          </section>
        </>
      )}

      <ChaoReportWorkModal
        open={modalReportId !== null}
        onClose={closeModal}
        unitId={unitId}
        reportId={modalReportId}
        unitLabel={unitName}
        initialTab={modalTab}
        onSubmitted={onReportSubmitted}
        onApproved={onReportApproved}
        onReturned={onReportReturned}
        onUpdated={() => void load()}
      />

      <ChaoDeObraReportModal
        open={galleryReport !== null}
        onClose={() => setGalleryReport(null)}
        unitId={unitId}
        report={galleryReport}
        unitLabel={unitName}
        onUpdated={() => void load()}
      />
    </div>
  );
}
